import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

// Üyenin aynı anda kaç sonuçlanmamış çekim talebi olabileceği.
// Değer `settings` kaydında değil `provider_configs`'tedir (bkz.
// backend/services/withdraw_rules.py), bu yüzden sayfanın ortak Kaydet
// düğmesine bağlanmaz: kendi ucu ve kendi düğmesi vardır.
export const WITHDRAW_PENDING_DEFAULT = 1;

export function parsePendingLimit(raw, cap) {
  const text = String(raw ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  // Sıfır geçersizdir: "sınırsız" da "kapalı" da okunabilirdi.
  if (value < 1 || (cap && value > cap)) return null;
  return value;
}

export default function WithdrawRulesSettings({ canWrite }) {
  const { toast } = useToast();
  const [saved, setSaved] = useState(null);
  const [value, setValue] = useState(String(WITHDRAW_PENDING_DEFAULT));
  const [cap, setCap] = useState(50);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    client.get('/admin/withdraw-rules')
      .then(({ data }) => {
        if (!alive) return;
        setSaved(data.max_pending_withdrawals);
        setValue(String(data.max_pending_withdrawals));
        if (data.max_cap) setCap(data.max_cap);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (saved === null) return null;

  const parsed = parsePendingLimit(value, cap);
  const dirty = parsed !== null && parsed !== saved;

  const save = async () => {
    if (parsed === null) return;
    setSaving(true);
    try {
      const { data } = await client.patch(
        '/admin/withdraw-rules', { max_pending_withdrawals: parsed });
      setSaved(data.max_pending_withdrawals);
      setValue(String(data.max_pending_withdrawals));
      toast({ title: 'Çekim talebi sınırı kaydedildi' });
    } catch (e) {
      toast({
        title: 'Çekim talebi sınırı kaydedilemedi',
        description: e.response?.data?.detail?.[0]?.msg
          || e.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="col-span-full">
      <label className="settings-field-label">
        Üye Başına Bekleyen Çekim Talebi Sınırı
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="number"
          min="1"
          max={cap}
          step="1"
          value={value}
          disabled={!canWrite}
          onChange={(e) => setValue(e.target.value)}
          className="admin-input admin-input-w-sm"
          aria-label="Üye başına bekleyen çekim talebi sınırı"
        />
        {canWrite && (
          <button
            type="button"
            className="admin-btn-primary flex items-center gap-2"
            onClick={save}
            disabled={!dirty || saving}
          >
            {saving
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Save className="h-4 w-4" />}
            Kaydet
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-400">
        Bir üyenin aynı anda sonuçlanmamış kaç çekim talebi olabileceği.
        Varsayılan 1'dir; artırırsanız üyenin bir yöntemdeki çekimi işlemdeyken
        başka bir yöntemden de talep açabilir. En az 1, en fazla {cap}.
      </p>
      {parsed === null && (
        <p className="mt-1 text-xs text-red-400">
          1 ile {cap} arasında bir tam sayı girin.
        </p>
      )}
    </div>
  );
}
